import { useCallback, useEffect, useState } from 'react';
import { vehicleDefectsService, type CriticalDefectsSummary } from '../../../services/vehicleDefectsService';
import { APPROVALS_CHANGED_EVENT } from '../../../components/awaitingApprovalsCount';

export interface UseCriticalDefectsResult {
  summary: CriticalDefectsSummary | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * EQ-30 Critical Defects widget: open Critical defects per service area, plus the total.
 * `enabled` follows View Repairs at the call site, so a user without it never sends the request.
 * Refetches when `notifyCriticalDefectsChanged()` fires.
 */
export function useCriticalDefects(enabled: boolean): UseCriticalDefectsResult {
  const [summary, setSummary] = useState<CriticalDefectsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchSummary = useCallback(async () => {
    if (!enabled) {
      setSummary(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setSummary(await vehicleDefectsService.criticalSummary());
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Could not load critical defects.'));
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onChanged = () => fetchSummary();
    window.addEventListener(APPROVALS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(APPROVALS_CHANGED_EVENT, onChanged);
  }, [enabled, fetchSummary]);

  return { summary, loading, error, refetch: fetchSummary };
}
